import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowRight, CalendarClock, WifiOff, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  scheduled: "Scheduled",
  new: "Scheduled",
  en_route: "En route",
  on_site: "On site",
  in_progress: "In progress",
  issue_reported: "Issue reported",
  completed_pending_review: "Ready to submit",
  completed: "Completed",
};

/** Shows the current status + a row of allowed-next-state buttons.
 *  Server is the source of truth — we always re-query the allowlist.
 *  Disabled while offline. */
export const StatusPanel = ({ jobId, onChanged }) => {
  const [state, setState] = useState({ loading: true, current: null, allowed: [] });
  const [busy, setBusy] = useState(false);
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/team/my/jobs/${jobId}/allowed-transitions`);
      setState({ loading: false, current: r.data.current, allowed: r.data.allowed });
    } catch (err) {
      setState({ loading: false, current: null, allowed: [], error: err?.response?.data?.detail || "Could not load status" });
    }
  };
  useEffect(() => {
    load();
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
    // eslint-disable-next-line
  }, [jobId]);

  const change = async (to) => {
    if (!online) return toast.error("Offline — status changes require connectivity.");
    setBusy(true);
    try {
      await axios.post(`${API}/team/my/jobs/${jobId}/status`, { to });
      toast.success(`Status → ${STATUS_LABELS[to] || to}`);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update status");
    } finally { setBusy(false); }
  };

  if (state.loading) return null;
  if (state.error) return (
    <div className="bg-red-50 border border-red-200 rounded-md p-3 text-xs" data-testid="status-panel-error">
      <AlertTriangle size={14} className="inline mr-1 text-red-600" /> {state.error}
    </div>
  );

  return (
    <div className="bg-white border border-[#cfd7db] rounded-md p-4" data-testid="status-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-display uppercase tracking-widest text-[#3f4b52]">Current status</span>
        <span data-testid="status-current" className="bg-[#111619] text-white px-2 py-1 rounded-sm font-display uppercase tracking-widest text-xs">
          {STATUS_LABELS[state.current] || state.current}
        </span>
      </div>
      {state.allowed.length > 0 ? (
        <div className="mt-3">
          <div className="text-xs font-display uppercase tracking-widest text-[#3f4b52] mb-2">Move to</div>
          <div className="flex flex-wrap gap-2">
            {state.allowed.map((s) => (
              <button key={s} onClick={() => change(s)} disabled={busy || !online}
                      data-testid={`status-to-${s}`}
                      className="inline-flex items-center gap-1 bg-[#f28a16] text-white px-3 py-1.5 rounded-md font-display uppercase tracking-widest text-xs disabled:opacity-50">
                {busy ? <Loader2 className="animate-spin" size={12} /> : <ArrowRight size={12} />} {STATUS_LABELS[s] || s}
              </button>
            ))}
          </div>
          {!online && (
            <div className="mt-3 text-[11px] text-red-600 inline-flex items-center gap-1" data-testid="status-offline-note">
              <WifiOff size={12} /> Status changes are disabled while offline
            </div>
          )}
        </div>
      ) : (
        <p className="mt-2 text-[11px] text-[#3f4b52]">No further status changes available from here — the admin office takes it from this point.</p>
      )}
    </div>
  );
};

/** Fitter-initiated schedule-change request. Admin has to approve.
 *  UI never fakes success while offline. */
export const ScheduleChangePanel = ({ jobId, currentDate, currentTime, onSubmitted }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ new_date: "", new_time: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/team/my/schedule-changes`);
      setMine(r.data.filter((x) => x.job_id === jobId));
    } catch { /* silent */ }
  };
  useEffect(() => {
    load();
    const up = () => setOnline(true); const down = () => setOnline(false);
    window.addEventListener("online", up); window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
    // eslint-disable-next-line
  }, [jobId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!online) return toast.error("Offline — schedule-change requests require connectivity.");
    if (!form.new_date) return toast.error("Choose a new date");
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.new_date)) return toast.error("Invalid date format");
    if (!form.reason || form.reason.trim().length < 3) return toast.error("Please give a reason");
    setBusy(true);
    try {
      await axios.post(`${API}/team/my/jobs/${jobId}/schedule-change`, form);
      toast.success("Request sent to admin");
      setOpen(false);
      setForm({ new_date: "", new_time: "", reason: "" });
      await load();
      onSubmitted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit request");
    } finally { setBusy(false); }
  };

  const pending = mine.find((m) => m.status === "pending");
  const history = mine.filter((m) => m.status !== "pending").slice(0, 3);

  return (
    <div className="bg-white border border-[#cfd7db] rounded-md p-4" data-testid="schedule-change-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-xs font-display uppercase tracking-widest text-[#3f4b52]">Fitment slot</div>
          <div className="font-display font-bold text-[#111619] text-lg" data-testid="schedule-current-slot">
            {currentDate || "—"} · {currentTime || "—"}
          </div>
        </div>
        {!pending && (
          <button onClick={() => setOpen((v) => !v)} disabled={!online}
                  data-testid="schedule-change-open"
                  className="inline-flex items-center gap-1 bg-white border border-[#f28a16] text-[#f28a16] px-3 py-1.5 rounded-md font-display uppercase tracking-widest text-xs disabled:opacity-50">
            <CalendarClock size={12} /> Request change
          </button>
        )}
      </div>

      {pending && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-md p-3 text-xs" data-testid="schedule-pending-req">
          <div className="font-display uppercase tracking-widest text-amber-800">Pending admin approval</div>
          <div className="mt-1 text-[#3f4b52]">
            Requested slot: <b>{pending.new_date} {pending.new_time || ""}</b> — {pending.reason}
          </div>
        </div>
      )}

      {open && !pending && (
        <form onSubmit={submit} className="mt-3 space-y-2 border-t border-[#cfd7db] pt-3" data-testid="schedule-change-form">
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-xs font-display uppercase tracking-widest text-[#3f4b52] block mb-1">New date</span>
              <input type="date" required value={form.new_date} onChange={(e) => setForm({ ...form, new_date: e.target.value })}
                     data-testid="schedule-change-date"
                     className="w-full h-10 border border-[#cfd7db] rounded-md px-2 text-sm" />
            </label>
            <label className="block">
              <span className="text-xs font-display uppercase tracking-widest text-[#3f4b52] block mb-1">New time (optional)</span>
              <input type="time" value={form.new_time} onChange={(e) => setForm({ ...form, new_time: e.target.value })}
                     data-testid="schedule-change-time"
                     className="w-full h-10 border border-[#cfd7db] rounded-md px-2 text-sm" />
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-display uppercase tracking-widest text-[#3f4b52] block mb-1">Reason</span>
            <textarea rows={2} required minLength={3} maxLength={500} value={form.reason}
                      onChange={(e) => setForm({ ...form, reason: e.target.value })}
                      data-testid="schedule-change-reason"
                      className="w-full border border-[#cfd7db] rounded-md p-2 text-sm" />
          </label>
          <div className="flex gap-2">
            <button type="button" onClick={() => setOpen(false)} className="flex-1 border rounded-md py-2 text-xs">Cancel</button>
            <button type="submit" disabled={busy || !online}
                    data-testid="schedule-change-submit"
                    className="flex-1 bg-[#f28a16] text-white rounded-md py-2 text-xs font-display uppercase tracking-widest disabled:opacity-60">
              {busy ? "Sending…" : "Send request"}
            </button>
          </div>
          {!online && <div className="text-[11px] text-red-600 inline-flex items-center gap-1"><WifiOff size={12} /> Offline — cannot send</div>}
        </form>
      )}

      {history.length > 0 && (
        <details className="mt-3 text-xs text-[#3f4b52]" data-testid="schedule-history">
          <summary className="cursor-pointer font-display uppercase tracking-widest">Past requests</summary>
          <ul className="mt-2 space-y-1">
            {history.map((h) => (
              <li key={h.id} className="flex items-start gap-2">
                <span className={`px-1.5 py-0.5 rounded-sm text-xs font-display uppercase tracking-widest ${h.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                  {h.status}
                </span>
                <span>{h.new_date} {h.new_time || ""} · {h.admin_note || h.reason}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};
